import dxDataGrid from "devextreme/ui/data_grid";
import { refreshCaseListFromDb } from "../case-list/case-list-detail";
import { toggleCaseDataValidationMode } from "./case-data/component";
import { caseDataValidationModes, defaultCaseData, formatCaseDataObject, getLineLevelChargesDataGridInstance, OutcomeTypes, SET_CASE_FORM_DATA } from "./case-data/reducers/case-data-reducer";
import { CASE_DETAIL_POPUP_MODES, saveCaseDataTab, toggleCaseDetailWarningPopup } from "./component";
import { initialCompleteCaseDetailData, RESET_CASE_CLAIM_DETAILS, SAVE_AND_CLOSE_BUTTON_CLICKABLE, SET_CASE_DATA_SAVED, SET_CASE_DETAIL_DATA, TOGGLE_CASE_DETAIL_POPUP } from "./reducers/case-detail-reducer";
import { first } from "lodash";
import { callOperation } from "async-ops";
import { SAVE_AUDIT } from "../services/save-audit";
import { formatAuditObjectToSave } from "../reducers/external-audit-reducer";
import { HIDE_LOADER, SHOW_LOADER } from "../../../common_components/mdae-common-reducer";
import { ATTACH_DOCUMENT } from "../../../common_components/document-management/services/attach-documents";
import { updateCount as updateeMDRMenuCount } from "../../emdr-inbox/inbox/component";
import { formatUSLocaleDate } from "../../../utils/dateUtils";
import { CASE_ADD_LOCATIONS } from "../../../common_components/add-case-manual/constant";

export const toggleCaseDetailPopup = (
    dispatch,
    isVisible,
    title = "Audit Case Details",
    data = null,
    eaCaseDetailsTabPanelSelectedIndex = 0
  ) => {
    if(isVisible){
      dispatch({
        type: SET_CASE_FORM_DATA,
        data: {
          caseData: {
            ...defaultCaseData
          },
        },
      });
      dispatch({
        type: RESET_CASE_CLAIM_DETAILS,
        data: {
          completeCaseDetail: {
            ...initialCompleteCaseDetailData
          },
        },
      });
      dispatch({
        type: SET_CASE_DATA_SAVED,
        data: { isCaseDataSaved: false },
      });
    }

    dispatch({
      type: TOGGLE_CASE_DETAIL_POPUP,
      data: {
        isCaseDetailPopupVisible: isVisible,
        caseDetailPopupTitle: title,
      },
    });
    setTimeout(() => {
      dispatch({
        type: TOGGLE_CASE_DETAIL_POPUP,
        data: {
          caseDetailPopupTitle: title,
        },
      });
      if (data) {
        dispatch({
          type: SET_CASE_DETAIL_DATA,
          data: {
            caseDetailData: data,
            eaCaseDetailsTabPanelSelectedIndex: eaCaseDetailsTabPanelSelectedIndex,
          },
        });
      }
    });
  };

export const validateAndSaveCaseData = (
  params,
  isCurrentRequesterReviewContractorValid: boolean,
  isCurrentContentTypeInvalidForReviewContractor: boolean,
  refreshCasesList: () => void,
  isSaveAndCloseButton: boolean = false,
  isSaveAndNext = false,
  validationMode: string = caseDataValidationModes.CASE_LEVEL,
  documentId = 0,
  advanceCaseToNextRound: boolean = false
) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    try {
      toggleCaseDataValidationMode(dispatch, validationMode);
      let result = null;
      if (params.validationGroup) result = params.validationGroup.validate();
      if (result && result.isValid) {
        if (
          isCurrentRequesterReviewContractorValid == false ||
          isCurrentContentTypeInvalidForReviewContractor == false
        ) {
          resolve(false);
          return;
        }
        const lineLevelChargeDataGridInstance = getLineLevelChargesDataGridInstance();
        let saveChangesPromise = Promise.resolve();
        if (lineLevelChargeDataGridInstance && lineLevelChargeDataGridInstance.hasEditData()) {
          saveChangesPromise = lineLevelChargeDataGridInstance.saveEditData();
        }
        saveChangesPromise.then(() => {
          let caseDataTabData = getState().caseDataReducer.caseData;
          const currentUser = getState().mdaeNavigationBarReducer.currentUser;
          if (Number(caseDataTabData?.isRoundLineLevelEnabledForAudit) === 1 && Array.isArray(caseDataTabData?.roundData)) {
            const outcomesState = getState().outcomesReducer;
            const rllOutcomeList = outcomesState?.roundLineLevelOutcomeList || [];
            const croOutcomeList = outcomesState?.caseRoundOutcomeList || [];
            caseDataTabData = {
              ...caseDataTabData,
              roundData: caseDataTabData.roundData.map(round => {
                const { isRoundOutcomeChangedByUser, ...roundWithoutFlag } = round;
                if (!isRoundOutcomeChangedByUser || !round.roundOutcomeId || !Array.isArray(round.roundCharges)) {
                  return roundWithoutFlag;
                }
                const selectedCRO = croOutcomeList.find(o => o.eaOutcomeId === round.roundOutcomeId);
                if (!selectedCRO?.outcomeType) return roundWithoutFlag;
                const matchingRLL = rllOutcomeList.find(o => o.eaOutcomeName === selectedCRO.eaOutcomeName && o.activeInd !== false);
                const outcomeType = selectedCRO.outcomeType;
                const rllOutcomeId = matchingRLL?.eaOutcomeId ?? null;
                const updatedCharges = round.roundCharges.map(charge => {
                  if (charge.roundOutcomeId) return charge;
                  const remaining = charge.reviewRemainingAmnt ?? 0;
                  const retained = charge.totalRetainedAmnt ?? 0;
                  const refund = charge.totalRefundAmnt ?? 0;
                  const amountFields =
                    outcomeType === OutcomeTypes.Approved
                      ? { totalRetainedAmnt: retained + remaining, reviewRemainingAmnt: 0 }
                      : outcomeType === OutcomeTypes.DeniedFinal
                        ? { totalRefundAmnt: refund + remaining, reviewRemainingAmnt: 0 }
                        : {};
                  return {
                    ...charge,
                    outcomeType,
                    roundOutcomeId: rllOutcomeId,
                    ...amountFields,
                  };
                });
                return { ...roundWithoutFlag, roundCharges: updatedCharges };
              }),
            };
          }
          if (Number(caseDataTabData?.isRoundLineLevelEnabledForAudit) === 1 && Array.isArray(caseDataTabData?.roundData)) {
            const allRoundCharges = caseDataTabData.roundData.flatMap(r => r?.roundCharges ?? []);

            if (allRoundCharges.length) {
              const sumField = (field: string) =>
                allRoundCharges.reduce((sum, charge) => sum + (Number(charge?.[field]) || 0), 0);

              const fieldsToAggregate = [
                "atRiskAmnt",
                "totalRefundAmnt",
                "totalRetainedAmnt",
                "totalRevenueLoss",
              ];

              const aggregated = fieldsToAggregate.reduce((obj, field) => {
                if (!Number(caseDataTabData?.[field])) {
                  obj[field] = sumField(field);
                }
                return obj;
              }, {} as any);

              if (Object.keys(aggregated).length) {
                const updatedData = { ...caseDataTabData, ...aggregated };

                caseDataTabData = {
                  ...updatedData,
                  reviewRemainingAmnt:
                    (Number(updatedData.atRiskAmnt) || 0) -
                    (Number(updatedData.totalRetainedAmnt) || 0) -
                    (Number(updatedData.totalRefundAmnt) || 0) -
                    (Number(updatedData.totalRevenueLoss) || 0),
                };
              }
            }
          }
          if (
            caseDataTabData.reviewRemainingAmnt !== 0 &&
            caseDataTabData.caseFinalOutcomeId !== 0 &&
            caseDataTabData.caseFinalOutcomeId != null &&
            caseDataTabData.moveInProgressDollarToType === "NOU" &&
            (caseDataTabData.previousCaseFinalOutcomeId == null ||
              caseDataTabData.previousCaseFinalOutcomeId == 0)
          ) {
            toggleCaseDetailWarningPopup(dispatch, true);
            dispatch({
              type: SAVE_AND_CLOSE_BUTTON_CLICKABLE,
              data: { isSaveAndCloseButtonClickable: isSaveAndCloseButton },
            });
            resolve(false);
          } else {
            dispatch({ type: SHOW_LOADER });
            const state = getState();
            const auditPropertiesData = state.auditPropertiesReducer.auditPropertiesData;
            const caseDetailPopupMode = state.caseDetailReducer.caseDetailData.mode
            let auditDataToSave = {
              ...formatAuditObjectToSave(auditPropertiesData, null),
              caseList: [],
              eaRounds: []
            };

            if (caseDetailPopupMode === CASE_DETAIL_POPUP_MODES.CREATE) {
              const completeCaseDetail = state.caseDetailReducer.completeCaseDetail;
              const caseSearchPageData = state.caseSearchPageReducer;
              const auditStatusList: any[] = state.auditPropertiesReducer.auditStatus;
              const caseStatusList: any[] = state.caseDataReducer.statusList;
              const caseDetailData = state.caseDetailReducer.caseDetailData

              let auditStatusId = null;
              if (caseStatusList.find(x => x.statusId === caseDataTabData.statusId)?.statusType?.toUpperCase() !== "NEW"
                || auditPropertiesData.startDate <= new Date()) {
                auditStatusId = auditStatusList.find(
                  (x) => x.statusCd.toUpperCase() === "IN-PROGRESS" && x.curated === "Y")?.statusId;
              } else {
                auditStatusId = auditStatusList.find(
                  (x) => x.statusCdtoUpperCase === "PREPARATION" && x.curated === "Y")?.statusId;
              }
              auditDataToSave = {
                ...auditDataToSave,
                serviceFromDate: formatUSLocaleDate(completeCaseDetail.statementFromDate ?? caseSearchPageData?.serviceFrom),
                serviceToDate: formatUSLocaleDate(completeCaseDetail.statementToDate ?? caseSearchPageData?.serviceTo),
                eaRequesterId: caseDataTabData.requesterId,
                eaContentTypeId: caseDataTabData.eaContentTypeId,
                statusId: auditStatusId,
                claimTypeCd: caseSearchPageData.claimTypeCd,
                title: `${completeCaseDetail.patientLastName}, ${completeCaseDetail.patientFirstName}`,
                documentId: documentId ?? 0,
                rqstrRecordSubmissionMethod: caseDataTabData.rqstrRecordSubmissionMethod,
                rqstrRecordSubmissionValue: caseDataTabData.rqstrRecordSubmissionValue,
                caseAddedFrom: CASE_ADD_LOCATIONS.CASE_SEARCH_PAGE,
                caseList: [formatCaseDataObject({ ...caseDetailData, ...caseDataTabData, eaCaseId: 0, auditGroupId: auditPropertiesData.auditGroupId })],
              }
              callOperation(SAVE_AUDIT, auditDataToSave).then((auditSaveResponse) => {
                if (isSaveAndCloseButton) toggleCaseDetailPopup(dispatch, false);
                let attachDocumentPromise = Promise.resolve();
                if (documentId) {
                  attachDocumentPromise = callOperation(ATTACH_DOCUMENT, {
                    documentId: documentId,
                    attachedLevelId: auditSaveResponse.auditId,
                    attachedLevelName: "EA",
                  });
                }
                return attachDocumentPromise.then(() => {
                  //open created audit
                  redirectToAudit(auditSaveResponse.auditId, (first<any>(auditSaveResponse.eaCaseIds)));
                  updateeMDRMenuCount(dispatch);
                  dispatch({ type: HIDE_LOADER });
                  resolve(true);
                });
              }).catch(reject);
            } else {
              let savePromise: Promise<any>[] = [];
              savePromise.push(saveCaseDataTab(
                dispatch,
                { ...caseDataTabData, advanceCaseToNextRound },
                currentUser,
                isSaveAndCloseButton,
                isSaveAndNext
              ));
              Promise.all(savePromise)
                .then(() => {
                  if (typeof refreshCasesList === "function")
                    refreshCasesList();
                  else refreshCaseListFromDb(dispatch, caseDataTabData?.eaAuditId);
                  if (isSaveAndNext) {
                    const caseListDataGridInstance = dxDataGrid.getInstance(
                      $("#pnlCaseListDetailGrid") as any
                    ) as dxDataGrid;
                    const caseListDataSourceObject = caseListDataGridInstance.getDataSource();
                    caseListDataSourceObject
                      .store()
                      .load({
                        group: caseListDataSourceObject.group(),
                        sort: caseListDataSourceObject.sort(),
                      })
                      .then((groupedData: any[]) => {
                        let caseListDataGridDataSource = [];
                        if ((first(groupedData) as any).items) {
                          caseListDataGridDataSource = getItemsFromGroupedDataSource(
                            groupedData
                          );
                        } else {
                          caseListDataGridDataSource = groupedData;
                        }

                        var caseIdIndex = caseListDataGridDataSource?.findIndex(
                          (x) => x.eaCaseId === caseDataTabData.eaCaseId
                        );
                        var nextEaCaseIdIndex = caseIdIndex + 1;
                        if (nextEaCaseIdIndex > caseListDataGridDataSource.length - 1)
                          nextEaCaseIdIndex = 0;
                        const nextEaCaseData =
                          caseListDataGridDataSource[nextEaCaseIdIndex];
                        if (nextEaCaseData) {
                          dispatch({ type: SHOW_LOADER });
                          toggleCaseDetailPopup(
                            dispatch,
                            true,
                            "Audit Case Details",
                            nextEaCaseData,
                            0
                          );
                        }
                        resolve(true);
                      }).catch(reject);
                  } else {
                    resolve(true);
                  }
                })
                .catch(reject);
            }
          }
        }).catch(reject);
      } else {
        (first(result?.brokenRules) as any)?.validator?.focus();
        resolve(false);
      }
    } catch (err) {
      reject(err);
    }
  });
}

export const redirectToAudit = (auditId, eaCaseId = null) => {
  window.open("/externalaudit/audit?auditId=" +
    auditId + (eaCaseId != null ? "&eacaseid=" + eaCaseId : ""), "_blank")
}

const getItemsFromGroupedDataSource = (item) => {
  if (item?.length > 0 && item[0].eaCaseId) return item;
  else if (item?.length > 0)
    return getItemsFromGroupedDataSource(item.flatMap((x) => x.items));
};